/**
 * Ergibt sich der gerenderte (unterschriebene) Text aus seiner Quelle? (Prüfung Vertragsbaukasten 0–2, Befund 1)
 *
 * Der Server rendert nicht selbst. Damit das Recht „Vertragstext ändern“ und der Hinweis „weicht von der Vorlage ab“ trotzdem
 * den Text schützen, der angezeigt, gedruckt, gehasht und unterschrieben wird, prüft die Datenbank, ob rendered_html (und der
 * vertrauliche Teil) eine Ausprägung von body_html (annex_html) ist: fester Text Zeichen für Zeichen gleich (bei Schweizer
 * Agenturen auch in Schweizer Schreibweise), Platzhalter nur an ihrer Stelle und nur mit dem, was der Renderer dort einsetzen
 * kann, „Nur wenn“ als einer der beiden Zweige, Unterschriftsfelder genau als Markup des Renderers. Die Quelle wird dafür wie
 * im Renderer zerlegt (contractSourceNodes, gleich app.contract_source_nodes in Migration 0115) und Stück für Stück gegen den
 * Text gelegt; verfolgt wird die Menge aller möglichen Stellen (kein regulärer Ausdruck über den ganzen Vertrag, der bei
 * langen Texten zu tief würde). Gleichlauf-Test in tests/db/99zzzzzzzz_behebung_vertrag_baukasten.test.ts.
 *
 * Seit der Prüfung Runde 2 (R2-01) entscheidet nicht mehr diese Formprüfung: Der Server rendert selbst mit den Stammdaten und
 * vergleicht Zeichen für Zeichen (contractRenderedFromSource unten, app.contract_rendered_matches und app.contract_render_check in
 * Migration 0119). Die Zerlegung (contractSourceNodes, contractNodesMatch) bleibt für die Anzeige der Stücke und den Gleichlauf.
 */
import { renderContract, swissGerman } from "./contract-render.js";
import { annexHasContent, CONTRACT_WS_CLASS, stripConfidentialPartNote } from "./contract-markup.js";
import { isContractRole, systemVariable, type ContractContext, type CustomVariableDef } from "./contract-variables.js";
import { isIsoDate } from "./periods.js";
import type { Currency, MoneyLocale } from "./types.js";

const WS_TRIM = new RegExp(`^${CONTRACT_WS_CLASS}+|${CONTRACT_WS_CLASS}+$`, "g");
const trimWs = (s: string) => s.replace(WS_TRIM, "");
const IF_RX = new RegExp(`^#if${CONTRACT_WS_CLASS}+[^\\t\\n\\v\\f\\r \\u00a0\\u1680\\u2000-\\u200a\\u2028\\u2029\\u202f\\u205f\\u3000\\ufeff]`);
const SAMPLE_NOTICE_RX = new RegExp(`<p class="tk-sample-notice">(?:[^<]|<(?!/p>))*</p>${CONTRACT_WS_CLASS}*`, "g");
const CONTENTLESS_RX = new RegExp(`<[^>]*>|&nbsp;|${CONTRACT_WS_CLASS}`, "g");

/** Was der Renderer an einem Platzhalter einsetzen kann */
export type SlotKind = "text" | "number" | "boolean";

/**
 * Stück der Quelle: fester Text (lit: Markup oder roher Platzhalter; text: Vorlagentext, alt = Schweizer Schreibweise, falls
 * anders), Platzhalter (slot) oder „Nur wenn“ mit beiden Zweigen.
 */
export type SourceNode =
  | { k: "lit"; s: string }
  | { k: "text"; s: string; alt: string | null }
  | { k: "slot"; kind: SlotKind; fill: string | null }
  | { k: "if"; then: SourceNode[]; else: SourceNode[] };

type Tok = { text: string } | { inner: string; raw: string };

function tokenize(src: string): Tok[] {
  const out: Tok[] = [];
  let last = 0;
  for (const m of src.matchAll(/\{\{([^{}]*)\}\}/g)) {
    if (m.index > last) out.push({ text: src.slice(last, m.index) });
    out.push({ inner: trimWs(m[1] ?? ""), raw: m[0] });
    last = m.index + m[0].length;
  }
  if (last < src.length) out.push({ text: src.slice(last) });
  return out;
}

/**
 * Art eines Platzhalters: Text (alles ohne < und >, Zeilenumbrüche als <br>); Geld, Datum, Prozent, Zahl (höchstens 40 Zeichen
 * ohne < > und Kleinbuchstaben); Ja/Nein (wie formatBoolean, auch groß). Geld, Zahl und Ja/Nein dürfen auch der Platzhalter
 * selbst bleiben (fehlender Wert; das sperrt der Versand ohnehin). Füllt eine Partei die Angabe, auch das Ausfüllfeld.
 */
function slotOf(path: string, filters: string[], defs: ReadonlyMap<string, CustomVariableDef>): SourceNode {
  const custom = path.startsWith("var.") ? defs.get(path.slice(4)) : undefined;
  const type = path.startsWith("var.") ? custom?.type : systemVariable(path)?.type;
  const fill = custom?.fillable_by && isContractRole(custom.fillable_by) ? custom.key : null;
  let kind: SlotKind = "text";
  if ((type === "money" || type === "date" || type === "percent" || type === "number") && filters.every((f) => f === "money" || f === "date" || f === "percent")) kind = "number";
  else if (type === "boolean" && filters.every((f) => f === "upper")) kind = "boolean";
  return { k: "slot", kind, fill };
}

/** Wie parseNodes im Renderer: offene oder überzählige Steuer-Platzhalter bleiben roh stehen. */
function parse(toks: Tok[], start: number, inIf: boolean, defs: ReadonlyMap<string, CustomVariableDef>):
  { nodes: SourceNode[]; pos: number; closedBy: "else" | "endif" | null } {
  const nodes: SourceNode[] = [];
  let pos = start;
  while (pos < toks.length) {
    const t = toks[pos]!;
    pos += 1;
    if ("text" in t) {
      const alt = swissGerman(t.text);
      nodes.push({ k: "text", s: t.text, alt: alt === t.text ? null : alt });
      continue;
    }
    const inner = t.inner;
    if (inner === "else" || inner === "/if") {
      if (inIf) return { nodes, pos, closedBy: inner === "else" ? "else" : "endif" };
      nodes.push({ k: "lit", s: t.raw });
      continue;
    }
    if (inner.startsWith("signature:")) {
      const role = trimWs(inner.slice(10));
      nodes.push({ k: "lit", s: isContractRole(role) ? `<div class="tk-signature" data-role="${role}"></div>` : t.raw });
      continue;
    }
    if (!IF_RX.test(inner)) {
      const parts = inner.split("|").map(trimWs);
      nodes.push(slotOf(parts[0] ?? "", parts.slice(1), defs));
      continue;
    }
    const thenPart = parse(toks, pos, true, defs);
    pos = thenPart.pos;
    let otherwise: SourceNode[] = [];
    let closedBy = thenPart.closedBy;
    let elseRaw: string | null = null;
    if (closedBy === "else") {
      const prev = toks[pos - 1];
      elseRaw = prev && "raw" in prev ? prev.raw : "{{else}}";
      const elsePart = parse(toks, pos, true, defs);
      pos = elsePart.pos;
      otherwise = elsePart.nodes;
      closedBy = elsePart.closedBy;
    }
    if (closedBy === "endif") { nodes.push({ k: "if", then: thenPart.nodes, else: otherwise }); continue; }
    nodes.push({ k: "lit", s: t.raw }, ...thenPart.nodes);
    if (elseRaw !== null) nodes.push({ k: "lit", s: elseRaw }, ...otherwise);
  }
  return { nodes, pos, closedBy: null };
}

/** Stücke der Quelle (ohne Hinweise im Vorlagenkopf), gleich app.contract_source_nodes. */
export function contractSourceNodes(source: string, defs: readonly CustomVariableDef[]): SourceNode[] {
  const map = new Map(defs.filter((d) => d && typeof d.key === "string").map((d) => [d.key, d]));
  return parse(tokenize(source.replace(SAMPLE_NOTICE_RX, "")), 0, false, map).nodes;
}

const BOOLEAN_WORDS = ["Ja", "Nein", "Yes", "No", "JA", "NEIN", "YES", "NO"].map((w) => Array.from(w));
const FORBIDDEN_NUMBER = /[<>a-z]/;
const BR = Array.from("<br>");
const SPAN_CLOSE = Array.from("</span>");

function startsAt(s: readonly string[], p: number, lit: readonly string[]): boolean {
  if (p + lit.length > s.length) return false;
  for (let i = 0; i < lit.length; i++) if (s[p + i] !== lit[i]) return false;
  return true;
}

/** Ende eines rohen Platzhalters „{{…}}“ ab p (ohne { } < > darin) oder null */
function rawPlaceholderEnd(s: readonly string[], p: number): number | null {
  if (s[p] !== "{" || s[p + 1] !== "{") return null;
  for (let i = p + 2; i < s.length; i++) {
    const c = s[i]!;
    if (c === "}") return s[i + 1] === "}" ? i + 2 : null;
    if (c === "{" || c === "<" || c === ">") return null;
  }
  return null;
}

function slotEnds(node: Extract<SourceNode, { k: "slot" }>, s: readonly string[], starts: readonly number[]): number[] {
  const out = new Set<number>();
  const fillOpen = node.fill === null ? null : Array.from(`<span class="tk-fill" data-var="${node.fill}">`);
  for (const p of starts) {
    if (node.kind === "text") {
      if (!out.has(p)) {
        let i = p;
        out.add(i);
        for (;;) {
          if (startsAt(s, i, BR)) i += 4;
          else if (i < s.length && s[i] !== "<" && s[i] !== ">") i += 1;
          else break;
          out.add(i);
        }
      }
    } else {
      if (node.kind === "number") {
        for (let j = 0; j <= 40 && p + j <= s.length; j++) {
          out.add(p + j);
          if (p + j >= s.length || FORBIDDEN_NUMBER.test(s[p + j]!)) break;
        }
      } else {
        for (const w of BOOLEAN_WORDS) if (startsAt(s, p, w)) out.add(p + w.length);
      }
      const raw = rawPlaceholderEnd(s, p);
      if (raw !== null) out.add(raw);
    }
    if (fillOpen && startsAt(s, p, fillOpen)) {
      let i = p + fillOpen.length;
      while (i < s.length && s[i] !== "<" && s[i] !== ">") i += 1;
      if (startsAt(s, i, SPAN_CLOSE)) out.add(i + SPAN_CLOSE.length);
    }
  }
  return [...out].sort((a, b) => a - b);
}

/** Alle Stellen, an denen die Stücke ab den Startstellen enden können (Menge, aufsteigend). */
function nodesEnds(nodes: readonly SourceNode[], s: readonly string[], starts: readonly number[]): number[] {
  let cur = [...starts];
  for (const n of nodes) {
    if (!cur.length) return cur;
    if (n.k === "lit" || n.k === "text") {
      const lits = [n.s, ...(n.k === "text" && n.alt !== null ? [n.alt] : [])].map((x) => Array.from(x));
      const next = new Set<number>();
      for (const p of cur) for (const l of lits) if (startsAt(s, p, l)) next.add(p + l.length);
      cur = [...next].sort((a, b) => a - b);
    } else if (n.k === "slot") cur = slotEnds(n, s, cur);
    else cur = [...new Set([...nodesEnds(n.then, s, cur), ...nodesEnds(n.else, s, cur)])].sort((a, b) => a - b);
  }
  return cur;
}

/** Folgt der Text aus den Stücken der Quelle (vom Anfang bis zum Ende)? */
export function contractNodesMatch(nodes: readonly SourceNode[], text: string): boolean {
  const s = Array.from(text);
  return nodesEnds(nodes, s, [0]).includes(s.length);
}

/**
 * Hat die Quelle Text, der in jedem Fall erscheint (außerhalb von „Nur wenn“ und Platzhaltern)? Ohne solchen Text darf der
 * gerenderte vertrauliche Teil fehlen (renderDraft speichert einen leeren Teil als null). Gleich app.contract_source_has_fixed_text.
 */
export function contractSourceHasFixedText(source: string): boolean {
  const fixed = contractSourceNodes(source, []).map((n) => (n.k === "text" || n.k === "lit" ? n.s : "")).join("");
  return fixed.replace(CONTENTLESS_RX, "") !== "";
}

/** Eigener Wert streng nach seinem Typ (Runde 2, R2-01; gleich app.csr_value_ok): Geld ganze Cent, Zahl und Prozent Zahl, Datum
 * ISO-Datum, Ja/Nein, sonst Text (oder eine ganze Zahl). Ein Geldfeld mit Text wäre sonst freier Text im Vertrag. */
export function contractValueTypeOk(v: unknown, type: string | undefined): boolean {
  switch (type) {
    case "money": return typeof v === "number" && Number.isInteger(v) && Math.abs(v) <= Number.MAX_SAFE_INTEGER;
    case "number": case "percent": return typeof v === "number" && Number.isFinite(v);
    case "date": return typeof v === "string" && v.length >= 10 && isIsoDate(v.slice(0, 10));
    case "boolean": return typeof v === "boolean";
    default: return typeof v === "string" || (typeof v === "number" && Number.isInteger(v) && Math.abs(v) < 1e15);
  }
}

const isBlank = (v: unknown) => v === undefined || v === null || (typeof v === "string" && v.trim() === "");
/** Varianten der Zahlformate verschiedener Browser (U+2019 statt ', U+202F statt U+00A0) gelten gleich (gleich app.csr_norm) */
const norm = (s: string) => s.replace(/\u2019/g, "'").replace(/\u202f/g, "\u00a0");

export interface RenderedFromSourceOptions {
  /** Werte der Systemplatzhalter (Stammdaten); ohne Kontext zählen nur Texte ohne Systemwerte als abgeleitet */
  context?: ContractContext;
  /** eigene Angaben des Vertrags */
  values?: Record<string, unknown>;
  locale?: MoneyLocale;
  currency?: Currency;
}

/**
 * Ist der gerenderte Teil genau der Text, den der Renderer aus Quelle, Kontext und eigenen Angaben erzeugt? (Runde 2, R2-01;
 * gleich app.contract_rendered_matches, mit dem die Datenbank jeden Entwurf und jeden Versand prüft.) Eigene Angaben zählen nur
 * streng nach Typ (contractValueTypeOk). Der vertrauliche Teil wird ohne die Kopfzeile des Versands verglichen; ohne Inhalt
 * gerendert genügt ein gespeicherter Teil ohne Inhalt (renderDraft speichert ihn als null).
 */
export function contractRenderedFromSource(source: string | null, rendered: string | null, defs: readonly CustomVariableDef[], part: "body" | "annex",
  opts: RenderedFromSourceOptions = {}): boolean {
  const src = source ?? "";
  const values = opts.values ?? {};
  const accessed = new Set<string>();
  const watched = new Proxy(values, { get: (t, k) => { if (typeof k === "string") accessed.add(k); return (t as Record<string, unknown>)[k as string]; } });
  const cleanDefs = (Array.isArray(defs) ? defs : []).filter((d) => d !== null && typeof d === "object" && typeof d.key === "string");
  const r = renderContract({ bodyHtml: part === "body" ? src : "", annexHtml: part === "annex" ? src : null, customVars: cleanDefs, values: watched,
    context: opts.context ?? {}, locale: opts.locale ?? "de-DE", currency: opts.currency ?? "EUR", view: "agency", annexHiddenFrom: [] });
  const byKey = new Map(cleanDefs.map((d) => [d.key, d]));
  for (const key of accessed) {
    const d = byKey.get(key);
    if (!d) continue;
    const v = values[key];
    if (!isBlank(v) ? !contractValueTypeOk(v, d.type) : !isBlank(d.default) && !contractValueTypeOk(d.default, d.type)) return false;
  }
  if (part === "annex") {
    const stored = rendered === null ? null : stripConfidentialPartNote(rendered);
    const out = src === "" ? null : r.annexHtml;
    // Runde 3, R3-03: ohne Inhalt gerendert genügt nur ein leerer Teil (oder genau der gerenderte), nicht jeder ohne sichtbaren Text
    if (!annexHasContent(out)) return stored === null || stored.replace(WS_TRIM, "") === "" || norm(stored) === norm(out ?? "");
    return norm(stored ?? "") === norm(out ?? "");
  }
  return norm(rendered ?? "") === norm(r.html);
}
